import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'

const BASE = '/api/v1/phone-calls/post-call-templates'

export type PostCallVariable = {
  key: string
  label: string
  value_type?: 'text' | 'bool' | 'number' | 'enum'
  enum_options?: string[]
  required?: boolean
  use_in_decision?: boolean
  source?: string
}

export type PostCallVariableDefinition = {
  id: string
  owner: number | null
  key: string
  label: string
  value_type: 'text' | 'bool' | 'number' | 'enum'
  enum_options: string[]
  required: boolean
  use_in_decision: boolean
  enabled: boolean
  source: string
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type PostCallTemplateOption = {
  id: string
  policy: string
  enabled: boolean
  waba_id: string
  template_name: string
  template_language: string
  meta_template_id: string
  meta_template_status: string
  body_parameter_count: number
  template_snapshot: Record<string, unknown>
  purpose_plain_text: string
  when_to_use_plain_text: string
  when_not_to_use_plain_text: string
  required_signals: string[]
  required_variables: string[]
  variable_mapping: Array<{
    position: number
    source_type: 'variable'
    source_key: string
    fallback_text?: string
    variable_key?: string
  }>
  priority: number
  fallback_template_name: string
  human_review_if_uncertain: boolean
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type PostCallTemplatePolicy = {
  id: string
  owner: number | null
  enabled: boolean
  min_confidence: number
  fallback_template_name: string
  low_confidence_behavior: string
  dedupe_window_hours: number
  require_whatsapp_opt_in: boolean
  metadata: Record<string, unknown>
  options: PostCallTemplateOption[]
  created_at: string
  updated_at: string
}

export type PostCallMetaTemplate = {
  id?: string
  name: string
  language: string
  status: string
  category: string
  components: Array<{ type: string; text?: string }>
  body_parameter_count: number
}

export type PostCallPreviewResult = {
  template_name: string
  template_language: string
  components: { body?: Array<{ type: string; text: string }> } | null
  resolved_variables: Record<string, string>
  missing_variables: string[]
  context_snapshot: Record<string, unknown>
}

export type PostCallCampaignAttempt = {
  id: string
  status: string
  channel: string
  provider: string
  error_code: string | null
  error_message: string
  created_at: string
}

export type PostCallDecision = {
  id: string
  call_id: string
  caller_phone: string | null
  elevenlabs_conversation_id: string | null
  status: string
  reason: string
  selected_template_name: string
  template_language: string
  selected_option_id: string | null
  confidence: number | null
  candidate_templates: string[]
  missing_variables: string[]
  resolved_variables: Record<string, unknown>
  campaign_execution_id: string | null
  campaign_execution_status: string | null
  campaign_dedupe_key: string | null
  campaign_attempts: PostCallCampaignAttempt[]
  decision_variables: Record<string, boolean>
  decision_variables_true: string[]
  selected_option_required_signals: string[]
  selected_option_signal_evaluation: Record<string, boolean>
  selected_option_trigger_signals: string[]
  campaign_result: Record<string, unknown>
  error_message: string
  created_at: string
}

class PostCallTemplateServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = getConversationsAuthToken()
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Token ${token}` } : {}),
        ...options.headers,
      },
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      const normalizeFieldErrors = (value: unknown): string[] => {
        if (!value) return []
        if (Array.isArray(value)) {
          return value.map((item) => String(item)).filter(Boolean)
        }
        if (typeof value === 'string') return [value]
        return []
      }
      const fieldErrors = Object.entries(payload as Record<string, unknown>)
        .flatMap(([field, value]) => normalizeFieldErrors(value).map((msg) => `${field}: ${msg}`))
        .join(' | ')
      const detail =
        (payload as { template?: string[]; error?: string; detail?: string }).template?.join(' ') ||
        (payload as { error?: string }).error ||
        (payload as { detail?: string }).detail ||
        fieldErrors ||
        response.statusText
      throw new Error(detail || `HTTP ${response.status}`)
    }

    if (response.status === 204) {
      return undefined as T
    }

    return response.json() as Promise<T>
  }

  getPolicy() {
    return this.request<PostCallTemplatePolicy>(`${BASE}/policy/`)
  }

  patchPolicy(data: Partial<PostCallTemplatePolicy>) {
    return this.request<PostCallTemplatePolicy>(`${BASE}/policy/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  listOptions() {
    return this.request<PostCallTemplateOption[]>(`${BASE}/options/`)
  }

  createOption(data: Partial<PostCallTemplateOption>) {
    return this.request<PostCallTemplateOption>(`${BASE}/options/`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  updateOption(id: string, data: Partial<PostCallTemplateOption>) {
    return this.request<PostCallTemplateOption>(`${BASE}/options/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  deleteOption(id: string) {
    return this.request<void>(`${BASE}/options/${id}/`, { method: 'DELETE' })
  }

  getVariables() {
    return this.request<{ variables: PostCallVariable[] }>(`${BASE}/variables/`)
  }

  listVariableDefinitions() {
    return this.request<PostCallVariableDefinition[]>(`${BASE}/variable-definitions/`)
  }

  createVariableDefinition(data: Partial<PostCallVariableDefinition>) {
    return this.request<PostCallVariableDefinition>(`${BASE}/variable-definitions/`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  updateVariableDefinition(id: string, data: Partial<PostCallVariableDefinition>) {
    return this.request<PostCallVariableDefinition>(`${BASE}/variable-definitions/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  deleteVariableDefinition(id: string) {
    return this.request<void>(`${BASE}/variable-definitions/${id}/`, { method: 'DELETE' })
  }

  getMetaTemplates(wabaId: string) {
    return this.request<{ data: PostCallMetaTemplate[] }>(
      `${BASE}/meta-templates/?waba_id=${encodeURIComponent(wabaId)}`,
    )
  }

  preview(payload: {
    option_id?: string
    waba_id?: string
    template_name?: string
    template_language?: string
    variable_mapping?: Array<{
      position: number
      source_type: 'variable'
      source_key: string
      fallback_text?: string
    }>
  }) {
    return this.request<PostCallPreviewResult>(`${BASE}/preview/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  listDecisions(params?: { status?: string; call_id?: string; limit?: number }) {
    const search = new URLSearchParams()
    if (params?.status) search.set('status', params.status)
    if (params?.call_id) search.set('call_id', params.call_id)
    if (params?.limit) search.set('limit', String(params.limit))
    const query = search.toString()
    return this.request<{ results: PostCallDecision[]; count: number }>(
      `${BASE}/decisions/${query ? `?${query}` : ''}`,
    )
  }

  testSend(optionId: string, payload: { phone_e164: string; confirmed: boolean }) {
    return this.request<{
      success: boolean
      is_test_send?: boolean
      execution_id?: string
      message_id?: string
      error?: string
      missing_variables?: string[]
    }>(`${BASE}/options/${optionId}/test-send/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }
}

export const postCallTemplateService = new PostCallTemplateServiceClient()
